import { Animation } from '@babylonjs/core/Animations/animation';
import { EasingFunction, SineEase } from '@babylonjs/core/Animations/easing';
import { StandardMaterial } from '@babylonjs/core/Materials/standardMaterial';
import { Color3 } from '@babylonjs/core/Maths/math.color';
import { Mesh } from '@babylonjs/core/Meshes/mesh';
import { VertexData } from '@babylonjs/core/Meshes/mesh.vertexData';
import { TransformNode } from '@babylonjs/core/Meshes/transformNode';
import { Scene } from '@babylonjs/core/scene';
import { KbViewer } from '@view/kb-viewer';
import { Subscription } from 'rxjs';
import { BoundingBoxInfo } from '../bounding-box-cache';

const SEGMENTS = 12;
const RADIUS = 0.05;
const DELTA_PHI = Math.PI / (2 * SEGMENTS);
const LINE_WIDTH = 0.02;
const FRAME_RATE = 1 / 0.3;

export class XrMarker extends Mesh {
    constructor(public v: KbViewer, public scene: Scene, public naturalScale: number) {
        super('xrMarker', scene);

        if (!XrMarker.MarkerMaterial) {
            const m = (XrMarker.MarkerMaterial = new StandardMaterial('xrMarkerMaterial', this.scene));
            m.emissiveColor = Color3.White();
            m.disableLighting = true;
            m.backFaceCulling = false;
            m.alpha = 0.5;
        }
        this.material = XrMarker.MarkerMaterial;

        this.resize();
    }

    private subscription: Subscription = new Subscription();
    static MarkerMaterial: StandardMaterial;
    private _lineWidth: number;
    private _radius: number;
    /**
     * Whether the marker and model have been placed yet.
     * The marker is put inside the scene root node so it transforms with
     * the model once placed.  So our calculations change based on that
     **/
    private _placed: boolean;

    public place(rootNode: TransformNode) {
        this.setParent(rootNode);
        this.rotation.set(0, 0, 0);
        this._placed = true;

        this.resize();
        this.hide();

        this.subscription.add(
            this.v.bbCache.rootBoundingBoxChanged.subscribe(bb => {
                this.resizeToBoundingBox(this.v.bbCache.getWorld(this.v.sceneNode)!);
            })
        );
    }

    public show() {
        this.animateVisibility(1, EasingFunction.EASINGMODE_EASEIN);
    }

    public hide() {
        this.animateVisibility(0, EasingFunction.EASINGMODE_EASEOUT);
    }

    private animateVisibility(to: number, easingFunction: number) {
        const a = new Animation('hide', 'visibility', FRAME_RATE, Animation.ANIMATIONTYPE_FLOAT);
        a.setKeys([
            {
                frame: 0,
                value: this.visibility,
            },
            {
                frame: 1,
                value: to,
            },
        ]);
        const easing = new SineEase();
        easing.setEasingMode(easingFunction);
        a.setEasingFunction(easing);
        this.scene.stopAnimation(this);
        this.scene.beginDirectAnimation(this, [a], 0, 1, false);
    }

    private resize() {
        const bb = this.v.bbCache.get(this.v.sceneNode)!;
        if (bb) {
            this.resizeToBoundingBox(bb);
        }
    }

    private resizeToBoundingBox(bb: BoundingBoxInfo) {
        let deltaZ: number;
        let deltaX: number;

        if (!this._placed) {
            deltaX = (bb.max.x - bb.min.x) * this.naturalScale;
            deltaZ = (bb.max.z - bb.min.z) * this.naturalScale;
            this._lineWidth = LINE_WIDTH;
            this._radius = RADIUS;
        } else {
            deltaX = bb.max.x - bb.min.x;
            deltaZ = bb.max.z - bb.min.z;
            this._lineWidth = LINE_WIDTH / this.naturalScale;
            this._radius = RADIUS / this.naturalScale;
        }

        const vertexData = this.getVertexData(deltaX, deltaZ);
        vertexData.applyToMesh(this, true);

        if (this._placed) {
            //it's in the root node now, so center to the root node local bb
            this.position.set(bb.center.x, 0, bb.center.z);
        }
    }

    protected getVertexData(xSize: number, zSize: number) {
        const vertices: number[] = [];
        const indices: number[] = [];
        const normals: number[] = [];

        const x = xSize / 2;
        const z = zSize / 2;
        this.addCorner(vertices, x, z);
        this.addCorner(vertices, -x, z);
        this.addCorner(vertices, -x, -z);
        this.addCorner(vertices, x, -z);

        const numVertices = vertices.length / 3;
        for (let i = 0; i < numVertices - 2; i += 2) {
            indices.push(i, i + 1, i + 3, i, i + 3, i + 2);
        }
        const i = numVertices - 2;
        indices.push(i, i + 1, 1, i, 1, 0);

        VertexData.ComputeNormals(vertices, indices, normals);
        const vdata = new VertexData();
        vdata.indices = indices;
        vdata.positions = vertices;
        vdata.normals = normals;

        return vdata;
    }

    protected addCorner(vertices: number[], cornerX: number, cornerZ: number) {
        let phi = cornerX > 0 ? (cornerZ > 0 ? 0 : -Math.PI / 2) : cornerZ > 0 ? Math.PI / 2 : Math.PI;
        for (let i = 0; i <= SEGMENTS; ++i) {
            vertices.push(
                cornerX + (this._radius - this._lineWidth) * Math.cos(phi),
                0,
                cornerZ + (this._radius - this._lineWidth) * Math.sin(phi),
                cornerX + this._radius * Math.cos(phi),
                0,
                cornerZ + this._radius * Math.sin(phi)
            );
            phi += DELTA_PHI;
        }
    }

    public dispose() {
        this.subscription.unsubscribe();
        super.dispose();
    }
}
